import { supabase } from '../supabase';
import { todayLocalDateStr } from './lifeAdminData';

/* ============================================
   CONSTANTS
   ============================================ */

export const DEFAULT_GOALS = {
  meals: 3,
  water_ml: 2000,
  exercise_min: 30,
};

export const MEAL_TYPES = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch',     label: 'Lunch' },
  { value: 'dinner',    label: 'Dinner' },
  { value: 'snack',     label: 'Snack' },
];

export const INTENSITY_LEVELS = [
  { value: 'low',    label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high',   label: 'High' },
];

export const EXERCISE_SUGGESTIONS = [
  'Walking', 'Running', 'Cycling', 'Yoga', 'Gym',
  'Swimming', 'Pilates', 'Hiking', 'Stretching',
];

export const WATER_QUICK_AMOUNTS = [100, 250, 500, 1000];

/* ============================================
   PROMPTS
   ============================================ */

export const HEALTH_INSIGHT_PROMPT = ({ recent, todaySummary, energyToday }) => `You are the Health module of Curx AI, an attentive coach helping the user notice patterns in their daily logs.

Read the user's last 7 days of food, water, and exercise data. Identify 2-3 patterns worth naming, then offer 2-3 specific, actionable suggestions.

Today's energy (1-10): ${energyToday ?? '(not logged)'}
Today so far: ${todaySummary.meals_count} meals, ${todaySummary.water_ml}ml water, ${todaySummary.exercise_min} min exercise

LAST 7 DAYS OF LOGS (most recent first):
${recent.length === 0 ? '(no logs in the past 7 days)' : recent.map(r => {
  const t = new Date(r.logged_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  if (r.log_type === 'food')     return `[${t}] FOOD · ${r.log_data?.meal_type || 'meal'}: ${r.log_data?.description || ''}${r.log_data?.calories ? ` (${r.log_data.calories} kcal)` : ''}`;
  if (r.log_type === 'water')    return `[${t}] WATER · ${r.log_data?.amount_ml || 0}ml`;
  if (r.log_type === 'exercise') return `[${t}] EXERCISE · ${r.log_data?.activity || 'activity'} · ${r.log_data?.duration_min || 0}min · ${r.log_data?.intensity || 'medium'}`;
  return `[${t}] ${r.log_type}`;
}).join('\n')}

Output ONLY valid JSON, no markdown:

{
  "observations": ["<1-sentence pattern 1>", "<pattern 2>", "<pattern 3>"],
  "suggestions": ["<actionable suggestion 1>", "<suggestion 2>", "<suggestion 3>"]
}

Be specific. Reference actual data the user logged. Do not be preachy. Skip platitudes.`;

/* ============================================
   SUPABASE — READ
   ============================================ */

export async function loadTodayLogs(userId, date = todayLocalDateStr()) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from('health_logs')
    .select('*')
    .eq('user_id', userId)
    .eq('date', date)
    .order('logged_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function loadRecentLogs(userId, days = 7) {
  if (!userId) return [];
  const since = new Date();
  since.setDate(since.getDate() - days);
  const sinceStr = since.toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from('health_logs')
    .select('*')
    .eq('user_id', userId)
    .gte('date', sinceStr)
    .order('logged_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/* ============================================
   AGGREGATIONS — pure functions on log arrays
   ============================================ */

export function sumWaterMl(logs) {
  return (logs || [])
    .filter(l => l.log_type === 'water')
    .reduce((sum, l) => sum + (Number(l.log_data?.amount_ml) || 0), 0);
}

export function sumExerciseMin(logs) {
  return (logs || [])
    .filter(l => l.log_type === 'exercise')
    .reduce((sum, l) => sum + (Number(l.log_data?.duration_min) || 0), 0);
}

export function countMeals(logs) {
  return (logs || []).filter(l => l.log_type === 'food').length;
}

// Compact summary for the Today tab's AI prompt. Returns null if zero logs.
export async function getTodayHealthSummary(userId, date = todayLocalDateStr()) {
  try {
    const logs = await loadTodayLogs(userId, date);
    if (!logs || logs.length === 0) return null;
    return {
      water_ml:     sumWaterMl(logs),
      exercise_min: sumExerciseMin(logs),
      meals_count:  countMeals(logs),
    };
  } catch (_) {
    return null;
  }
}

/* ============================================
   SUPABASE — WRITE (with .select() RLS verification)
   ============================================ */

async function insertLog({ user_id, log_type, log_data, notes, date = todayLocalDateStr() }) {
  if (!user_id) throw new Error('Missing user_id');
  const payload = {
    user_id,
    date,
    log_type,
    log_data: log_data || {},
    notes: notes || null,
  };
  const { data, error } = await supabase
    .from('health_logs')
    .insert(payload)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Save didn't write any rows. Check your RLS policy for INSERT on health_logs.");
  }
  return data[0];
}

export async function logFood({ user_id, meal_type, description, calories, notes }) {
  return insertLog({
    user_id,
    log_type: 'food',
    log_data: {
      meal_type:   String(meal_type || 'snack').trim(),
      description: String(description || '').trim(),
      ...(calories ? { calories: Number(calories) || 0 } : {}),
    },
    notes,
  });
}

export async function logWater({ user_id, amount_ml }) {
  const ml = Math.max(0, Math.round(Number(amount_ml) || 0));
  if (ml <= 0) throw new Error('Water amount must be positive');
  return insertLog({
    user_id,
    log_type: 'water',
    log_data: { amount_ml: ml },
  });
}

export async function logExercise({ user_id, activity, duration_min, intensity, notes }) {
  return insertLog({
    user_id,
    log_type: 'exercise',
    log_data: {
      activity:     String(activity || '').trim(),
      duration_min: Math.max(1, Math.round(Number(duration_min) || 0)),
      intensity:    String(intensity || 'medium').toLowerCase(),
    },
    notes,
  });
}

export async function deleteLog(logId) {
  if (!logId) throw new Error('Missing log id');
  const { data, error } = await supabase
    .from('health_logs')
    .delete()
    .eq('id', logId)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Delete didn't affect any rows. Check your RLS policy for DELETE on health_logs.");
  }
}

/* ============================================
   AI INSIGHT
   ============================================ */

export async function generateHealthInsight({ recent, todaySummary, energyToday }) {
  const prompt = HEALTH_INSIGHT_PROMPT({
    recent: recent || [],
    todaySummary: todaySummary || { meals_count: 0, water_ml: 0, exercise_min: 0 },
    energyToday,
  });
  const response = await fetch('/api/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, model: 'sonnet' }),
  });
  const data = await response.json();
  const text = (data?.content || []).map(i => i.text || '').join('').trim();
  const parsed = tryParseJSONObject(text);
  if (!parsed || !Array.isArray(parsed.observations) || !Array.isArray(parsed.suggestions)) {
    throw new Error('Health insight returned invalid shape');
  }
  return {
    observations: parsed.observations.map(s => String(s).trim()).filter(Boolean).slice(0, 3),
    suggestions:  parsed.suggestions.map(s => String(s).trim()).filter(Boolean).slice(0, 3),
  };
}

/* ============================================
   HELPERS
   ============================================ */

function tryParseJSONObject(text) {
  if (!text) return null;
  try { return JSON.parse(text); } catch (_) { /* fallthrough */ }
  const match = String(text).match(/\{[\s\S]*\}/);
  if (match) {
    try { return JSON.parse(match[0]); } catch (__) { /* fallthrough */ }
  }
  return null;
}

export function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const t = new Date(timestamp);
  const now = new Date();
  const diffMin = Math.floor((now - t) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function describeLog(log) {
  if (!log) return '';
  if (log.log_type === 'food') {
    const d = log.log_data || {};
    const meal = d.meal_type ? d.meal_type.charAt(0).toUpperCase() + d.meal_type.slice(1) : 'Meal';
    return `${meal} · ${d.description || '(no description)'}${d.calories ? ` · ${d.calories} kcal` : ''}`;
  }
  if (log.log_type === 'water') {
    return `Water · ${log.log_data?.amount_ml || 0}ml`;
  }
  if (log.log_type === 'exercise') {
    const d = log.log_data || {};
    return `${d.activity || 'Activity'} · ${d.duration_min || 0}min · ${d.intensity || 'medium'}`;
  }
  return log.log_type;
}

// 7-day window (Mon-Sun) ending today, returns array of {date, label, isToday, dayOfWeek}.
// Caller can filter logs by .date to render per-day stats.
export function buildHistoryWeek() {
  const today = new Date();
  const todayDow = today.getDay(); // 0=Sun..6=Sat
  // Days since Monday: if today=Mon (1), 0; if Sun (0), 6; otherwise dow-1
  const daysSinceMonday = (todayDow + 6) % 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysSinceMonday);
  monday.setHours(0, 0, 0, 0);
  const days = [];
  const labels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const todayStr = todayLocalDateStr();
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    days.push({
      date: dateStr,
      label: labels[i],
      dayNum: d.getDate(),
      isToday: dateStr === todayStr,
      isFuture: d > today,
    });
  }
  return days;
}

export function classifyDay({ meals, waterMl, exerciseMin }, goals = DEFAULT_GOALS) {
  const total = (meals > 0 ? 1 : 0) + (waterMl > 0 ? 1 : 0) + (exerciseMin > 0 ? 1 : 0);
  if (total === 0) return 'empty';
  const goalsHit =
    (meals      >= goals.meals        ? 1 : 0) +
    (waterMl    >= goals.water_ml     ? 1 : 0) +
    (exerciseMin>= goals.exercise_min ? 1 : 0);
  if (goalsHit >= 2) return 'good';
  return 'partial';
}
